/**
 * Visual Effects System
 * Provides immersive visual feedback for game interactions
 */

import * as THREE from 'three'


/**
 * Advanced Visual Effects Manager
 */
export class VisualEffectsManager {
  private _scene: THREE.Scene
  private _onFrame: (() => void) | null

  constructor(scene: THREE.Scene, _renderer: THREE.WebGLRenderer, onFrame?: () => void) {
    this._scene = scene
    this._onFrame = onFrame ?? null
  }

  /**
   * Create shimmering effect for integer points
   */
  createShimmerEffect(position: THREE.Vector3): THREE.Group {
    const group = new THREE.Group()
    
    // Create multiple layers of shimmering particles
    for (let layer = 0; layer < 3; layer++) {
      const particleCount = 20
      const particles = new THREE.BufferGeometry()
      const positions = new Float32Array(particleCount * 3)
      const colors = new Float32Array(particleCount * 3)
      
      for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3
        
        // Random position around the integer point
        const radius = 0.5 + layer * 0.3
        const theta = Math.random() * Math.PI * 2
        const phi = Math.random() * Math.PI
        
        positions[i3] = position.x + radius * Math.sin(phi) * Math.cos(theta)
        positions[i3 + 1] = position.y + radius * Math.sin(phi) * Math.sin(theta)
        positions[i3 + 2] = position.z + radius * Math.cos(phi)
        
        // Golden shimmer color
        colors[i3] = 1.0 // Red
        colors[i3 + 1] = 0.8 // Green
        colors[i3 + 2] = 0.2 // Blue
      }
      
      particles.setAttribute('position', new THREE.BufferAttribute(positions, 3))
      particles.setAttribute('color', new THREE.BufferAttribute(colors, 3))
      
      const material = new THREE.PointsMaterial({
        size: 0.1 - layer * 0.02,
        vertexColors: true,
        transparent: true,
        opacity: 0.8 - layer * 0.2,
        blending: THREE.AdditiveBlending,
        depthWrite: false
      })
      
      const points = new THREE.Points(particles, material)
      group.add(points)
    }
    
    // Add animation
    const animate = () => {
      group.rotation.y += 0.01
      group.rotation.x += 0.005
      
      group.children.forEach((child, index) => {
        if (child instanceof THREE.Points) {
          child.material.opacity = 0.6 + 0.4 * Math.sin(Date.now() * 0.003 + index)
        }
      })
      this._onFrame?.()
    }
    
    const animationId = setInterval(animate, 16)
    
    // Clean up animation after 5 seconds
    setTimeout(() => {
      clearInterval(animationId)
      if (group.parent) {
        group.parent.remove(group)
      }
    }, 5000)
    
    // Automatically add to scene
    this._scene.add(group)
    return group
  }

  /**
   * Create explosion effect for cutting operations
   */
  createExplosionEffect(position: THREE.Vector3, intensity: number = 1.0): THREE.Group {
    const group = new THREE.Group()
    
    // Create particle explosion
    const particleCount = Math.floor(50 * intensity)
    const particles = new THREE.BufferGeometry()
    const positions = new Float32Array(particleCount * 3)
    const velocities = new Float32Array(particleCount * 3)
    const colors = new Float32Array(particleCount * 3)
    
    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3
      
      // Start at explosion center
      positions[i3] = position.x
      positions[i3 + 1] = position.y
      positions[i3 + 2] = position.z
      
      // Random velocity directions
      const theta = Math.random() * Math.PI * 2
      const phi = Math.random() * Math.PI
      const speed = (Math.random() * 0.5 + 0.5) * intensity
      
      velocities[i3] = speed * Math.sin(phi) * Math.cos(theta)
      velocities[i3 + 1] = speed * Math.sin(phi) * Math.sin(theta)
      velocities[i3 + 2] = speed * Math.cos(phi)
      
      // Orange/red explosion colors
      colors[i3] = 1.0
      colors[i3 + 1] = 0.3 + Math.random() * 0.4
      colors[i3 + 2] = 0.1
    }
    
    particles.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    particles.setAttribute('color', new THREE.BufferAttribute(colors, 3))
    
    const material = new THREE.PointsMaterial({
      size: 0.15,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    })
    
    const points = new THREE.Points(particles, material)
    group.add(points)
    
    // Animate explosion
    let time = 0
    const animate = () => {
      time += 0.05
      
      const positionAttribute = particles.getAttribute('position') as THREE.BufferAttribute
      const positionArray = positionAttribute.array as Float32Array
      
      for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3
        
        positionArray[i3] += velocities[i3] * 0.1
        positionArray[i3 + 1] += velocities[i3 + 1] * 0.1
        positionArray[i3 + 2] += velocities[i3 + 2] * 0.1
        
        // Add gravity
        velocities[i3 + 1] -= 0.002
        
        // Fade out
        material.opacity = Math.max(0, 0.8 - time * 0.4)
      }
      
      positionAttribute.needsUpdate = true
      this._onFrame?.()
      
      if (time > 2) {
        clearInterval(animationId)
        if (group.parent) {
          group.parent.remove(group)
        }
      }
    }
    
    const animationId = setInterval(animate, 16)
    
    // Add explosion group to scene
    this._scene.add(group)
    return group
  }

  /**
   * Create screen shake effect
   */
  screenShake(camera: THREE.Camera, intensity: number = 0.1, duration: number = 500): void {
    const originalPosition = camera.position.clone()
    const startTime = Date.now()
    
    const shake = () => {
      const elapsed = Date.now() - startTime
      const progress = elapsed / duration
      
      if (progress < 1) {
        const currentIntensity = intensity * (1 - progress)
        
        camera.position.x = originalPosition.x + (Math.random() - 0.5) * currentIntensity
        camera.position.y = originalPosition.y + (Math.random() - 0.5) * currentIntensity
        camera.position.z = originalPosition.z + (Math.random() - 0.5) * currentIntensity

        requestAnimationFrame(shake)
        this._onFrame?.()
      } else {
        camera.position.copy(originalPosition)
        this._onFrame?.()
      }
    }
    
    shake()
  }

  /**
   * Create floating number effect
   */
  createFloatingNumber(position: THREE.Vector3, value: number, color: string = '#ffffff'): void {
    const numberDiv = document.createElement('div')
    numberDiv.textContent = `+${value}`
    numberDiv.style.position = 'fixed'
    numberDiv.style.color = color
    numberDiv.style.fontSize = '24px'
    numberDiv.style.fontWeight = 'bold'
    numberDiv.style.fontFamily = 'Arial, sans-serif'
    numberDiv.style.textShadow = '2px 2px 4px rgba(0,0,0,0.5)'
    numberDiv.style.pointerEvents = 'none'
    numberDiv.style.zIndex = '1000'
    numberDiv.style.opacity = '1'
    numberDiv.style.transition = 'all 2s ease-out'
    
    // Convert 3D position to screen coordinates
    const canvas = document.querySelector('canvas')
    if (!canvas) return
    
    const rect = canvas.getBoundingClientRect()
    const x = rect.left + (position.x + 1) * rect.width / 2
    const y = rect.top + (1 - position.y) * rect.height / 2
    
    numberDiv.style.left = `${x}px`
    numberDiv.style.top = `${y}px`
    
    document.body.appendChild(numberDiv)
    
    // Remove after animation
    setTimeout(() => {
      if (numberDiv.parentNode) {
        numberDiv.parentNode.removeChild(numberDiv)
      }
    }, 2000)
  }
}

// Global instances

export let visualEffectsManager: VisualEffectsManager | null = null

export function initializeVisualEffects(scene: THREE.Scene, renderer: THREE.WebGLRenderer, onFrame?: () => void): void {
  visualEffectsManager = new VisualEffectsManager(scene, renderer, onFrame)
}
